import { groupColors } from "@/configs/map/groups";
import { useDataflowStore } from "@/stores/dataflow";
import type {
  LayerConfig,
  UnifiedGroupState,
} from "@/stores/map/types/layerState";
import type {
  MapLayerActions,
  MapLayerData,
  MapLayerState,
} from "@/types/stores/map";
import { computeBrickLayerCounts } from "@/utils/data/brickLayerCounts";
import { extractUniqueValuesFromGeoJSON } from "@/utils/data/geography";
import { shouldComputeChunks } from "@/utils/data/chunking";
import { logger } from "@/utils/debug";

export function createGeoJSONLayerActions(
  set: any,
  get: () => MapLayerState & MapLayerActions
) {
  return {
    addGeoJSONLayer: (layerId: string, layerData: MapLayerData) => {
      const state = get();

      Object.keys(state.mapInstances).forEach((workspaceId) => {
        get().removeTemporaryAiMarker(workspaceId);
      });

      set(
        (state: MapLayerState) => ({
          geoJSONLayers: {
            ...state.geoJSONLayers,
            [layerId]: layerData,
          },
        }),
        false,
        "map/addGeoJSONLayer"
      );

      if (layerData?.data?.features) {
        const workspaceId = useDataflowStore.getState().activeDataflowId;
        if (workspaceId) {
          // Get fresh state after layer is added to ensure we have the latest groupColumnName
          const currentState = get();
          const groupState = currentState.groups[workspaceId];
          const groupColumnName = groupState?.groupColumnName;
          if (groupColumnName) {
            const uniqueValues = extractUniqueValuesFromGeoJSON(
              layerData.data
            );
            if (shouldComputeChunks(uniqueValues.length)) {
              // Always compute chunks from GeoJSON data - this is the correct source of truth
              // Check if chunks already exist and match current values to avoid unnecessary recalculation
              const existingChunks = groupState?.chunks;
              const sortedValues = [...uniqueValues].sort();
              const newValuesKey = sortedValues.join(",");
              const existingValuesKey = existingChunks
                ? existingChunks
                    .flatMap((c) => c.values)
                    .sort()
                    .join(",")
                : null;
              
              // Only compute chunks if they don't exist or values have changed
              // Always use GeoJSON data as the source of truth for chunk computation
              if (newValuesKey !== existingValuesKey) {
                get().computeChunks(workspaceId, uniqueValues);
              }
            } else {
              // For non-chunked case, ensure unified groups exist if they don't already
              // This handles the case where GeoJSON data is updated after setGroupColumnName
              const unifiedGroups = currentState.unifiedGroups[workspaceId];
              if (
                !unifiedGroups ||
                !unifiedGroups.groups ||
                unifiedGroups.groups.length === 0
              ) {
                // Unified groups should have been created in setGroupColumnName,
                // but if they don't exist, create them here as a fallback
                const sortedUniqueValues = [...uniqueValues].sort();
                const unifiedGroupsArray: UnifiedGroupState["groups"] =
                  sortedUniqueValues.map((value, index) => {
                    const config = groupState?.groupConfigs?.[value];
                    return {
                      id: value,
                      type: "group" as const,
                      label: value,
                      values: [value],
                      color:
                        config?.color ||
                        groupColors[index % groupColors.length],
                      shape: config?.shape,
                      name: config?.name,
                    };
                  });

                const initialGroupConfigs: Record<string, LayerConfig> = {
                  ...(groupState?.groupConfigs || {}),
                };
                unifiedGroupsArray.forEach((group) => {
                  const existingConfig = initialGroupConfigs[group.id];
                  if (!existingConfig) {
                    initialGroupConfigs[group.id] = {
                      color: group.color,
                      shape: group.shape,
                      name: group.displayName || group.label,
                    };
                  } else {
                    initialGroupConfigs[group.id] = {
                      ...existingConfig,
                      color: existingConfig.color?.trim() || group.color,
                      shape: existingConfig.shape || group.shape,
                      name: existingConfig.name || group.displayName || group.label,
                    };
                  }
                });

                const unifiedGroupState: UnifiedGroupState = {
                  groupColumnName,
                  groups: unifiedGroupsArray,
                  groupConfigs: initialGroupConfigs,
                  layerConfig: groupState?.layerConfig,
                  visualizationMode: groupState?.visualizationMode,
                  visualizationLayerType: groupState?.visualizationLayerType,
                  categoryColorMap: groupState?.categoryColorMap,
                };

                set(
                  (state: MapLayerState) => {
                    const updatedLayerVisibility: Record<string, boolean> = {
                      ...state.layerVisibility,
                    };
                    unifiedGroupsArray.forEach((group) => {
                      const unifiedId = `group:${workspaceId}:${group.id}`;
                      updatedLayerVisibility[unifiedId] = true;
                    });

                    const newVisibleGroups = unifiedGroupsArray.map(
                      (group) => group.id
                    );

                    return {
                      groups: {
                        ...state.groups,
                        [workspaceId]: {
                          ...state.groups[workspaceId],
                          visibleGroups: newVisibleGroups,
                        },
                      },
                      unifiedGroups: {
                        ...state.unifiedGroups,
                        [workspaceId]: unifiedGroupState,
                      },
                      layerVisibility: updatedLayerVisibility,
                    };
                  },
                  false,
                  "map/updateGeoJSONLayerData-createUnifiedGroups"
                );
              }
            }
          }
        }
      }
    },

    removeGeoJSONLayer: (layerId: string) => {
      const state = get();
      const newGeoJSONLayers = { ...state.geoJSONLayers };
      delete newGeoJSONLayers[layerId];
      set(
        { geoJSONLayers: newGeoJSONLayers },
        false,
        "map/removeGeoJSONLayer"
      );
    },

    updateGeoJSONLayerVisibility: (layerId: string, isVisible: boolean) => {
      set(
        (state: MapLayerState) => ({
          geoJSONLayers: {
            ...state.geoJSONLayers,
            [layerId]: {
              ...state.geoJSONLayers[layerId],
              isVisible,
            },
          },
        }),
        false,
        "map/updateGeoJSONLayerVisibility"
      );
    },

    updateGeoJSONLayerData: (layerId: string, data: any) => {
      set(
        (state: MapLayerState) => ({
          geoJSONLayers: {
            ...state.geoJSONLayers,
            [layerId]: {
              ...state.geoJSONLayers[layerId],
              data,
            },
          },
        }),
        false,
        "map/updateGeoJSONLayerData"
      );

      if (data?.features) {
        const workspaceId = useDataflowStore.getState().activeDataflowId;
        if (workspaceId) {
          const state = get();
          const groupState = state.groups[workspaceId];
          const visualizationMode = groupState?.visualizationMode;
          const visualizationLayerType = groupState?.visualizationLayerType;
          
          // If in brick layer mode, recompute individual regions when GeoJSON data updates
          if (visualizationMode === "layers" && visualizationLayerType) {
            const layerGeoJSON = state.staticData[visualizationLayerType];
            if (layerGeoJSON) {
              // Compute individual region data with point counts (async to avoid blocking UI)
              computeBrickLayerCounts(data, layerGeoJSON, visualizationLayerType).then((regions) => {
                // Filter out regions with 0 points and sort by point count (descending) then by display name
                const regionsWithPoints = regions
                .filter((region) => region.pointCount > 0)
                .sort((a, b) => {
                  // First sort by point count (descending)
                  if (b.pointCount !== a.pointCount) {
                    return b.pointCount - a.pointCount;
                  }
                  // Then sort by display name
                  const nameA = a.displayName || a.regionId;
                  const nameB = b.displayName || b.regionId;
                  return nameA.localeCompare(nameB);
                });

              // Create individual groups for each region
              const unifiedGroups: UnifiedGroupState["groups"] = regionsWithPoints.map(
                (region, index) => {
                  const regionId = region.regionId;
                  const pointCount = region.pointCount;
                  
                  // Use existing layerRegionConfig if available, otherwise create new
                  const existingRegionConfig = groupState?.layerRegionConfigs?.[regionId];
                  const existingGroupConfig = groupState?.groupConfigs?.[regionId];
                  
                  // Build label: display name (if available) + point count, or just point count
                  const baseLabel = region.displayName || regionId;
                  const label = `${baseLabel} (${pointCount})`;
                  
                  // Assign color: use existing config color, or assign based on point count
                  let color: string;
                  if (existingRegionConfig?.color) {
                    color = existingRegionConfig.color;
                  } else if (existingGroupConfig?.color) {
                    color = existingGroupConfig.color;
                  } else {
                    // Assign color based on point count (use index for consistent coloring)
                    color = groupColors[index % groupColors.length];
                  }
                  
                  return {
                    id: regionId,
                    type: "group" as const,
                    label,
                    values: [regionId],
                    color,
                    shape: existingGroupConfig?.shape,
                    displayName: existingRegionConfig?.displayName || existingGroupConfig?.name || label,
                  };
                }
              );

              // Create layerRegionConfigs for each region (used by map for coloring)
              const initialLayerRegionConfigs: Record<
                string,
                { displayName?: string; color?: string; isVisible?: boolean }
              > = {
                ...(groupState?.layerRegionConfigs || {}),
              };
              
              // Create groupConfigs for each region (used by sidebar)
              const initialGroupConfigs: Record<string, LayerConfig> = {
                ...(groupState?.groupConfigs || {}),
              };
              
              unifiedGroups.forEach((group) => {
                const regionId = group.id;
                const region = regionsWithPoints.find((r) => r.regionId === regionId);
                
                if (region) {
                  // Update layerRegionConfigs
                  const existingRegionConfig = initialLayerRegionConfigs[regionId];
                  if (!existingRegionConfig) {
                    initialLayerRegionConfigs[regionId] = {
                      displayName: region.displayName || group.displayName,
                      color: group.color,
                      isVisible: true,
                    };
                  } else {
                    // Preserve existing config but update color if not set
                    initialLayerRegionConfigs[regionId] = {
                      ...existingRegionConfig,
                      color: existingRegionConfig.color || group.color,
                      displayName: existingRegionConfig.displayName || region.displayName || group.displayName,
                    };
                  }
                  
                  // Update groupConfigs
                  const existingGroupConfig = initialGroupConfigs[regionId];
                  if (!existingGroupConfig) {
                    initialGroupConfigs[regionId] = {
                      color: group.color,
                      shape: group.shape,
                      name: group.displayName,
                    };
                  } else {
                    initialGroupConfigs[regionId] = {
                      ...existingGroupConfig,
                      color: existingGroupConfig.color?.trim() || group.color,
                      shape: existingGroupConfig.shape || group.shape,
                      name: existingGroupConfig.name || group.displayName,
                    };
                  }
                }
              });

              // Update visible groups to include all regions
              const newVisibleGroups = unifiedGroups.map((group) => group.id);

              // Update layer visibility for all regions
              const updatedLayerVisibility: Record<string, boolean> = {
                ...state.layerVisibility,
              };
              unifiedGroups.forEach((group) => {
                const unifiedId = `group:${workspaceId}:${group.id}`;
                updatedLayerVisibility[unifiedId] = true;
              });

              // Update unified groups
              set(
                (state: MapLayerState) => ({
                  groups: {
                    ...state.groups,
                    [workspaceId]: {
                      ...state.groups[workspaceId],
                      visibleGroups: newVisibleGroups,
                      layerRegionConfigs: initialLayerRegionConfigs,
                    },
                  },
                  unifiedGroups: {
                    ...state.unifiedGroups,
                    [workspaceId]: {
                      ...state.unifiedGroups[workspaceId],
                      groups: unifiedGroups,
                      groupConfigs: initialGroupConfigs,
                    },
                  },
                  layerVisibility: updatedLayerVisibility,
                }),
                false,
                "map/updateGeoJSONLayerData-recomputeBrickLayerRegions"
              );
              }).catch((error) => {
                logger.error("[MapStore] Failed to recompute brick layer regions:", error);
              });
            }
          }
          
          const groupColumnName = groupState?.groupColumnName;
          if (groupColumnName) {
            const uniqueValues = extractUniqueValuesFromGeoJSON(data);
            if (shouldComputeChunks(uniqueValues.length)) {
              // Always compute chunks from GeoJSON data - this is the correct source of truth
              // Check if chunks already exist and match current values to avoid unnecessary recalculation
              const existingChunks = groupState?.chunks;
              const sortedValues = [...uniqueValues].sort();
              const newValuesKey = sortedValues.join(",");
              const existingValuesKey = existingChunks
                ? existingChunks
                    .flatMap((c) => c.values)
                    .sort()
                    .join(",")
                : null;
              
              // Only compute chunks if they don't exist or values have changed
              // Always use GeoJSON data as the source of truth for chunk computation
              if (newValuesKey !== existingValuesKey) {
                get().computeChunks(workspaceId, uniqueValues);
              }
            } else {
              // For non-chunked case, ensure unified groups exist if they don't already
              // This handles the case where GeoJSON data is updated after setGroupColumnName
              const unifiedGroups = state.unifiedGroups[workspaceId];
              if (
                !unifiedGroups ||
                !unifiedGroups.groups ||
                unifiedGroups.groups.length === 0
              ) {
                // Unified groups should have been created in setGroupColumnName,
                // but if they don't exist, create them here as a fallback
                const sortedUniqueValues = [...uniqueValues].sort();
                const unifiedGroupsArray: UnifiedGroupState["groups"] =
                  sortedUniqueValues.map((value, index) => {
                    const config = groupState?.groupConfigs?.[value];
                    return {
                      id: value,
                      type: "group" as const,
                      label: value,
                      values: [value],
                      color:
                        config?.color ||
                        groupColors[index % groupColors.length],
                      shape: config?.shape,
                      name: config?.name,
                    };
                  });

                const initialGroupConfigs: Record<string, LayerConfig> = {
                  ...(groupState?.groupConfigs || {}),
                };
                unifiedGroupsArray.forEach((group) => {
                  const existingConfig = initialGroupConfigs[group.id];
                  if (!existingConfig) {
                    initialGroupConfigs[group.id] = {
                      color: group.color,
                      shape: group.shape,
                      name: group.displayName || group.label,
                    };
                  } else {
                    initialGroupConfigs[group.id] = {
                      ...existingConfig,
                      color: existingConfig.color?.trim() || group.color,
                      shape: existingConfig.shape || group.shape,
                      name: existingConfig.name || group.displayName || group.label,
                    };
                  }
                });

                const unifiedGroupState: UnifiedGroupState = {
                  groupColumnName,
                  groups: unifiedGroupsArray,
                  groupConfigs: initialGroupConfigs,
                  layerConfig: groupState?.layerConfig,
                  visualizationMode: groupState?.visualizationMode,
                  visualizationLayerType: groupState?.visualizationLayerType,
                  categoryColorMap: groupState?.categoryColorMap,
                };

                set(
                  (state: MapLayerState) => {
                    const updatedLayerVisibility: Record<string, boolean> = {
                      ...state.layerVisibility,
                    };
                    unifiedGroupsArray.forEach((group) => {
                      const unifiedId = `group:${workspaceId}:${group.id}`;
                      updatedLayerVisibility[unifiedId] = true;
                    });

                    const newVisibleGroups = unifiedGroupsArray.map(
                      (group) => group.id
                    );

                    return {
                      groups: {
                        ...state.groups,
                        [workspaceId]: {
                          ...state.groups[workspaceId],
                          visibleGroups: newVisibleGroups,
                        },
                      },
                      unifiedGroups: {
                        ...state.unifiedGroups,
                        [workspaceId]: unifiedGroupState,
                      },
                      layerVisibility: updatedLayerVisibility,
                    };
                  },
                  false,
                  "map/updateGeoJSONLayerData-createUnifiedGroups"
                );
              }
            }
          }
        }
      }
    },

    setFolderName: (workspaceId: string, folderId: string, name: string) => {
      set(
        (state: MapLayerState) => ({
          folderNames: {
            ...state.folderNames,
            [workspaceId]: {
              ...(state.folderNames[workspaceId] || {}),
              [folderId]: name,
            },
          },
        }),
        false,
        "map/setFolderName"
      );
    },

    getFolderName: (workspaceId: string, folderId: string) => {
      const state = get();
      return state.folderNames[workspaceId]?.[folderId];
    },
  };
}

